// The record of the INBOX (docs/design/inbox.md §6; round 089 cards 24,
// 27 and 28): the head, the views UNREAD, TODAY and ALL, the bucket
// filter, the
// rows, the folds that unfold in place (comp B) and the open message as
// a page (089 · 24 b). The overlay owns the render and hands every press
// whose act starts with `ib-r-` to the route at the end of this file.
//
// The state of the view lives here and nowhere else: the view, the filter,
// the open message, the unfolded folds. The server owns every message and
// every read; a read the server has not answered yet lives in the store
// (`state.inboxReads`, audit P5).

import './record.css';
import { INBOX_BUCKETS, type InboxBucket } from '../../gen/balance';
import { CHROME_INBOX_REFETCH_MS } from '../../gen/presentation';
import type { InboxMsg } from '../../gen/proto/aquaphobia';
import { briefing as BS, inbox as S } from '../../gen/strings';
import type { GameState } from '../../state';
import { isoDate } from '../../locale/stamp';
import { esc } from '../esc';
import { tabStrip } from '../choice/choice';
import { hubSectionHref, hubTabHref } from '../hublink';
import type { RanksBoard, TabKey } from '../hub/panel';
import { assignWithGesture } from '../nav';
import { openHtml } from './open';
import type { InboxCtx } from './page';
import { onInboxPress } from './press';
import { dayWord } from './needsyou';
import { sinceLeftKeep } from './sinceleft';
import {
  bucketIcon, bucketName, bucketOf, bucketTag, isBucket, membersHtml, noteReplaceSent, replacePending, rowHtml,
} from './rows';
import { fig, nowSec } from './words';

/** The three views of the head (089 · 27): TODAY keeps the messages of
 *  the reader's calendar day (inbox.md §6). */
export type RecordViewName = 'unread' | 'today' | 'all';

const VIEWS: readonly RecordViewName[] = ['unread', 'today', 'all'];

/** The state of the record's view. */
interface RecordView {
  view: RecordViewName;
  /** The bucket of the filter row, or null for ALL. */
  bucket: InboxBucket | null;
  /** An event filter inside the bucket: WAR · REPORTS (089 · 08). */
  only: string | null;
  /** The id of the open message, 0 for the list. */
  open: number;
  /** The fold rows that stand unfolded. */
  unfolded: Set<number>;
  /** Where the list stood when a message opened. */
  listTop: number;
}

const view: RecordView = { view: 'all', bucket: null, only: null, open: 0, unfolded: new Set(), listTop: 0 };

/** Sets the view and the filter, with no message open and no fold unfolded. */
function setView(name: RecordViewName, bucket: InboxBucket | null, only: string | null): void {
  view.view = name;
  view.bucket = bucket;
  view.only = only;
  view.open = 0;
  view.unfolded.clear();
}

/** The view, for a test and for the overlay. */
export function recordView(): Readonly<RecordView> {
  return view;
}

/** Back to the first view of a page load, for a test. */
export function resetRecord(): void {
  setView('all', null, null);
  view.listTop = 0;
  affordSeen.clear();
  refetchDue = false;
  lastRefetchMs = 0;
}

/**
 * The INBOX opened: on UNREAD while anything is unread, else on ALL
 * (089 · 27), with no filter, no open message and no fold unfolded.
 */
export function recordOnOpen(s: GameState): void {
  setView(s.inboxUnread > 0 ? 'unread' : 'all', null, null);
}

/** WAR · REPORTS is the INBOX filtered to the battle reports of the WAR
 *  bucket (089 · 08). The overlay opens the INBOX after this. */
export function showBattleReports(): void {
  setView('all', 'war', 'battle_report');
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

/** Every message, newest first: a fold stands at the stamp of its last
 *  member (inbox.md §4). */
function listOf(s: GameState): InboxMsg[] {
  return [...s.inbox].sort((a, b) => Number(b.unix) - Number(a.unix) || b.id - a.id);
}

/** Is a filter standing: a bucket, WAR · REPORTS, or SHOW ONLY THESE of
 *  SINCE YOU LEFT (inbox.md §7)? */
function filtered(c: InboxCtx): boolean {
  return view.bucket !== null || view.only !== null || sinceLeftKeep(c) !== null;
}

/** Does the view in force keep a message? TODAY keeps a message whose
 *  stamp falls on the local date of the wall clock, the rule by which
 *  the stamp column shows a time and not a date. A fold stands at the
 *  stamp of its last member, so a fold that grew today stands in TODAY. */
function inViewNow(m: InboxMsg, s: GameState): boolean {
  switch (view.view) {
    case 'unread': return !s.inboxIsRead(m);
    case 'today': return isoDate(Number(m.unix)) === isoDate(nowSec());
    case 'all': return true;
  }
}

/** The rows of the view in force, and the rows the filters show of them:
 *  the bucket, WAR · REPORTS and SHOW ONLY THESE of SINCE YOU LEFT. */
export function shownRows(c: InboxCtx): { inView: InboxMsg[]; shown: InboxMsg[] } {
  const s = c.state;
  const keep = sinceLeftKeep(c);
  const inView = listOf(s).filter((m) => inViewNow(m, s) && (keep === null || keep(m)));
  const shown = inView.filter((m) => (view.bucket === null || bucketOf(m) === view.bucket)
    && (view.only === null || m.event === view.only));
  return { inView, shown };
}

// ---------------------------------------------------------------------------
// The REPLACE refusal (audit P3)
// ---------------------------------------------------------------------------

/** What `canAfford` said for each open REPLACE offer at the last render. */
const affordSeen = new Map<number, boolean>();
let refetchDue = false;
let lastRefetchMs = 0;

/**
 * The server states the refusal of a REPLACE when it sends the list, and
 * the client knows only whether the stores pay. When that flips for an
 * offer while the INBOX stands, the list is fetched again, so a "short of
 * alloy" does not stay after the stores refilled. The fetch keeps the
 * floor of the badge's refetch (R694).
 */
function watchAfford(c: InboxCtx): void {
  const s = c.state;
  for (const m of s.inbox) {
    const r = m.replace;
    if (!r || r.queued) continue;
    const afford = s.canAfford(r.price);
    const was = affordSeen.get(m.id);
    if (was !== undefined && was !== afford) refetchDue = true;
    affordSeen.set(m.id, afford);
  }
  const now = Date.now();
  if (refetchDue && now - lastRefetchMs >= CHROME_INBOX_REFETCH_MS) {
    refetchDue = false;
    lastRefetchMs = now;
    c.actions.inboxFetch();
  }
}

// ---------------------------------------------------------------------------
// The markup
// ---------------------------------------------------------------------------

/** The open message as a page, or '' while the list stands. A message
 *  that left the list (its lifetime ended) gives the list back. */
export function openPageHtml(c: InboxCtx): string {
  if (view.open === 0) return '';
  const m = c.state.inbox.find((x) => x.id === view.open);
  if (!m) {
    view.open = 0;
    return '';
  }
  return openHtml(m, c.state);
}

/** One chip of the filter row: ALL or a bucket, with the count of its
 *  rows in the view in force (audit P9). The row is the one filter of
 *  seven that stays a row of buttons, because a bucket icon and its count
 *  tell the player where the news is before a press (R2038). */
function chip(b: InboxBucket | null, n: number): string {
  const on = view.bucket === b;
  const label = b === null ? `<b>${esc(S.filter_all)}</b>` : bucketIcon(b, 14) + (on ? `<b>${esc(bucketTag(b))}</b>` : '');
  const title = b === null ? S.filter_all_title : S.filter_title({ bucket: bucketName(b) });
  return `<button class="ib-fc${b ? ` b-${b}` : ''}${on ? ' on' : ''}${n ? '' : ' none'}"`
    + ` data-act="ib-r-filter" data-k="${b ?? ''}" aria-pressed="${on}" title="${esc(title)}"`
    + ` aria-label="${esc(S.filter_label({ bucket: b === null ? S.filter_all : bucketName(b), n }))}">`
    + `${label}<i>${n ? fig(n) : '–'}</i></button>`;
}

/**
 * The head of the record (089 · 27), one row: INBOX, the views UNREAD,
 * TODAY and ALL, with the unread count after the word UNREAD and none at 0,
 * and MARK ALL READ at the right end, which reads MARK THESE READ while a
 * filter stands (P12). The filter row under it holds the count of rows
 * of each bucket. It stands at the top of the page, over SINCE YOU LEFT
 * and NEEDS YOU.
 */
export function recordHeadHtml(c: InboxCtx): string {
  const s = c.state;
  const unread = s.inboxUnread;
  const { inView, shown } = shownRows(c);
  const anyUnread = shown.some((m) => !s.inboxIsRead(m));
  const mark = s.inboxFetched && anyUnread
    ? `<button class="ib-p ib-mark" data-act="ib-r-mark">${esc(filtered(c) ? S.mark_these : S.mark_all)}</button>`
    : '';
  const unreadLabel = unread > 0 ? S.view_unread_count({ n: fig(unread) }) : S.view_unread;
  // UNREAD, TODAY and ALL pick the rows the record shows and save nothing: a tab
  // strip of the choice kit (R2011).
  const views = tabStrip([
    { id: 'unread', label: unreadLabel, title: S.view_unread_title },
    { id: 'today', label: S.view_today, title: S.view_today_title },
    { id: 'all', label: S.view_all, title: S.view_all_title },
  ], view.view, 'ib-r-view', { label: S.title });
  const head = `<div class="ib-head"><h3 class="ib-t">${esc(S.title)}</h3>${views}${mark}</div>`;
  const counts = new Map<InboxBucket, number>();
  for (const m of inView) {
    const b = bucketOf(m);
    counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  const chips = chip(null, inView.length) + INBOX_BUCKETS.map((b) => chip(b, counts.get(b) ?? 0)).join('');
  // A filter of the record has its line and the press that removes it;
  // SHOW ONLY THESE of SINCE YOU LEFT has its line, and its block holds
  // the press (inbox.md §7).
  const only = view.only !== null
    ? `<div class="ib-only"><span>${esc(S.only_battle)}</span>`
      + `<button class="ib-p" data-act="ib-r-only">${esc(S.show_all)}</button></div>`
    : sinceLeftKeep(c) !== null ? `<div class="ib-only"><span>${esc(S.only_range)}</span></div>` : '';
  return `<div class="ib-rec-head">${head}<div class="ib-fr" role="group">${chips}</div>${only}</div>`;
}

/**
 * The rows of the record: two lines in 44 px each, a fold unfolded in
 * place under its row. Before the first list arrives it says that it
 * waits (audit P7); an empty view says why it is empty (P8). A day head
 * stands before the rows of each local day of the reader's wall clock:
 * TODAY, YESTERDAY or the date (audit P10, R3136). TODAY holds one day,
 * so its rows take no head.
 */
export function recordHtml(c: InboxCtx): string {
  const s = c.state;
  watchAfford(c);
  if (!s.inboxFetched) return `<div class="ib-rows"><div class="ib-empty">${esc(S.loading)}</div></div>`;
  const { shown } = shownRows(c);
  if (shown.length === 0) {
    const why = s.inbox.length === 0 ? S.empty
      : filtered(c) ? S.empty_filter
        : view.view === 'today' ? S.empty_today : S.empty_unread;
    return `<div class="ib-rows"><div class="ib-empty">${esc(why)}</div></div>`;
  }
  const row = (m: InboxMsg): string => {
    const read = s.inboxIsRead(m);
    const unfolded = m.count >= 2 && view.unfolded.has(m.id);
    return rowHtml(m, s, { read, unfolded, after: unfolded ? membersHtml(m, s, read) : '' });
  };
  if (view.view === 'today') {
    return `<div class="ib-rows" role="list" aria-label="${esc(S.title)}">${shown.map(row).join('')}</div>`;
  }
  return `<div class="ib-rows">${dayGroups(shown, nowSec()).map((g) => `<h4 class="ib-day">${esc(g.word)}</h4>`
    + `<div class="ib-dl" role="list" aria-label="${esc(g.word)}">${g.rows.map(row).join('')}</div>`).join('')}</div>`;
}

/** The rows of a list, newest first, in one group per local day of the
 *  wall clock, each with the word of its day head. */
export function dayGroups(rows: readonly InboxMsg[], now: number): { word: string; rows: InboxMsg[] }[] {
  const out: { word: string; rows: InboxMsg[] }[] = [];
  let last = '';
  for (const m of rows) {
    const unix = Number(m.unix);
    const day = isoDate(unix);
    if (day !== last || out.length === 0) {
      out.push({ word: dayWord(unix, now), rows: [] });
      last = day;
    }
    out[out.length - 1].rows.push(m);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The presses
// ---------------------------------------------------------------------------

/** Marks messages read: one intent for the server, a set of ids in one
 *  action that the world answers with one list (R1851), and the local
 *  read that keeps the list and the counts true until it answers (P5). */
function read(c: InboxCtx, ids: readonly number[]): void {
  const s = c.state;
  const fresh = ids.filter((id) => {
    const m = s.inbox.find((x) => x.id === id);
    return m !== undefined && !s.inboxIsRead(m);
  });
  if (fresh.length === 0) return;
  if (fresh.length === 1) c.actions.inboxRead(fresh[0], false);
  else c.actions.inboxReadMany(fresh);
  s.markInboxRead(fresh);
}

/** MARK ALL READ, or MARK THESE READ while a filter stands: the second
 *  marks the shown rows alone, in one action (audit P12). */
function markShown(c: InboxCtx): void {
  if (!filtered(c)) {
    c.actions.inboxRead(0, true);
    c.state.markInboxRead('all');
    return;
  }
  read(c, shownRows(c).shown.map((m) => m.id));
}

/** The scroll host of a press, the tab body. */
function hostOf(el: HTMLElement): HTMLElement | null {
  return el.closest<HTMLElement>('#tabbody');
}

/**
 * Opens a message as its page, and reads it (round 089 card 24 b). The
 * act `ib-r-open` calls it, and so do NEEDS YOU and the inbox pins.
 * `host` is the tab body the press stood in; a call from outside the
 * INBOX (a pin on the map) gives none, and the INBOX opens first. False
 * when the list holds no message of that id.
 */
export function openRecordMessage(id: number, c: InboxCtx, host: HTMLElement | null = null): boolean {
  const m = c.state.inbox.find((x) => x.id === id);
  if (!m) return false;
  if (!host) c.openPanel('INBOX', '');
  view.listTop = host?.scrollTop ?? 0;
  view.open = id;
  // With the link down the page opens and the message stays unread: no
  // read went out, so none is kept (review K3).
  if (c.actions.linkUp()) read(c, [id]);
  // The page opens at its head. The overlay draws it after this call.
  if (host) queueMicrotask(() => { host.scrollTop = 0; });
  return true;
}

/** The presses that send an intent to the world. With the link down they
 *  send nothing, the player hears so in the words of SINCE YOU LEFT, and
 *  no local read is kept (audit P2, review K3). */
const SENDS: ReadonlySet<string> = new Set([
  'ib-r-mark', 'ib-r-read', 'ib-r-replay', 'ib-r-mreplay', 'ib-r-replace', 'ib-r-ransom',
]);

/** Where a hub press of a message leads, by its `data-k`. */
export function hubPressHref(k: string): string {
  switch (k) {
    case 'record': return hubSectionHref('record', 'achievements');
    case 'seasons': return hubSectionHref('ranks', 'seasons' satisfies RanksBoard);
    default: return hubTabHref('clan' satisfies TabKey);
  }
}

function pressRecord(act: string, el: HTMLElement, c: InboxCtx): void {
  const id = Number(el.dataset.id ?? 0);
  const n = Number(el.dataset.n ?? 0);
  const s = c.state;
  if (SENDS.has(act) && !c.actions.linkUp()) {
    c.actions.refuse(BS.link_down);
    return;
  }
  switch (act) {
    case 'ib-r-open': openRecordMessage(id, c, hostOf(el)); break;
    case 'ib-r-back': {
      view.open = 0;
      const host = hostOf(el);
      const top = view.listTop;
      if (host) queueMicrotask(() => { host.scrollTop = top; });
      break;
    }
    case 'ib-r-fold':
      if (view.unfolded.has(id)) view.unfolded.delete(id);
      else view.unfolded.add(id);
      break;
    case 'ib-r-view': view.view = VIEWS.find((v) => v === el.dataset.id) ?? 'all'; break;
    case 'ib-r-filter': {
      const k = el.dataset.k ?? '';
      const b = isBucket(k) ? k : null;
      view.bucket = view.bucket === b ? null : b;
      view.only = null;
      break;
    }
    case 'ib-r-only': view.only = null; view.bucket = null; break;
    case 'ib-r-mark': markShown(c); break;
    // READ on a fold reads it whole: the fold is one message.
    case 'ib-r-read': read(c, [id]); break;
    case 'ib-r-goto':
    case 'ib-r-show':
      if (n > 0) {
        c.actions.focusCell(n);
        c.collapse?.();
      }
      break;
    case 'ib-r-replay': {
      const ref = s.inbox.find((m) => m.id === id)?.replay;
      if (ref) c.actions.openReplay(ref);
      break;
    }
    case 'ib-r-mreplay': {
      const ref = s.inbox.find((m) => m.id === id)?.members[n]?.replay;
      if (ref) c.actions.openReplay(ref);
      break;
    }
    // One press, one intent: a second press waits for the list (P15).
    case 'ib-r-replace':
      if (!replacePending(id, s)) {
        noteReplaceSent(id, s);
        c.actions.inboxReplace(id);
      }
      break;
    case 'ib-r-ransom': if (id > 0) c.actions.ransom(id); break;
    // A pledge is made on the object's card of WAR · STRUCTURES, so PLEDGE
    // A FLEET moves the map to the object and opens that pill.
    case 'ib-r-pledge':
      if (n > 0) c.actions.focusCell(n);
      c.openPanel('WAR', 'STRUCTURES');
      break;
    case 'ib-r-captains': c.openPanel('CAPTAIN', 'PRISON'); break;
    // An achievement means ACHIEVEMENTS of RECORD (§16.16), and the season
    // report the SEASONS board of RANKS (R3136). The squad mates of a
    // pickup squad stand beside every section of a CLAN with no clan, so
    // that press names no section.
    case 'ib-r-hub':
      assignWithGesture(hubPressHref(el.dataset.k ?? ''));
      break;
  }
}

onInboxPress('ib-r-', pressRecord);
